import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import { useAdminSchoolsAndGrades } from '../../../hooks/useAdminQuery';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  Drawer,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  BarChart as StatisticsIcon,
  School as SchoolIcon,
  ExitToApp as ExitToAppIcon,
  Face,
  Logout,
  Assessment as ComprehensiveIcon,
  Monitor as MonitorIcon,
  BugReport as BugReportIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import ProfileModal from '../../components/modals/ProfileModal';
import LogoutModal from '../../components/modals/LogoutModal';
import SidebarHeader from '../../components/admin/SidebarHeader';
import SchoolSelector from '../../components/admin/SchoolSelector';
import NavigationMenu from '../../components/admin/NavigationMenu';
import UserSection from '../../components/admin/UserSection';
import AvatarMenu from '../../components/admin/AvatarMenu';
import { ROLES } from '@shared/types';


/**
 * סיידבר האדמין - רכיב סיידבר מלא לדשבורד האדמין
 */
const AdminPanelSidebar = ({ children }: { children: React.ReactNode }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isRtl = i18n.language === 'he';
  const userStore = useStoreByName('userStore');
  const adminStore = useStoreByName('adminStore');

  // בדיקת סוג המשתמש
  const userRoleId = userStore.userData?.personalInfo?.role?.id;
  const isAdmin = userRoleId === ROLES.ADMIN;
  const isPrincipal = userRoleId === ROLES.PRINCIPAL;

  // מצב תפריט המבורגר
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  // מצב תפריט האווטר
  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState<null | HTMLElement>(null);
  // מצב מודאל הפרופיל
  const [openProfileModal, setOpenProfileModal] = useState(false);
  // מצב מודאל התנתקות
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  // מצב אוואטר
  const [currentAvatarId, setCurrentAvatarId] = useState<number | null>(null);

  // מצב הסיידבר
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [schoolSelectorExpanded, setSchoolSelectorExpanded] = useState(false);

  // Sync avatar id from user data
  useEffect(() => {
    const avatarIdFromUser = userStore.userData?.personalInfo?.avatar_id;
    if (typeof avatarIdFromUser === 'number' && avatarIdFromUser > 0) {
      setCurrentAvatarId(avatarIdFromUser);
    } else {
      setCurrentAvatarId(null);
    }
  }, [userStore.userData?.personalInfo?.avatar_id]);

  // TanStack Query automatically loads schools and syncs to adminStore
  useAdminSchoolsAndGrades();

  const handleExitToApp = () => {
    navigate('/home');
  };

  const handleMenuItemClick = (path: string, isExternal?: boolean, openInNewTab?: boolean) => {
    if (isExternal) {
      if (openInNewTab) {
        // פתיחת קישור חיצוני בטאב חדש (למשל Sentry)
        window.open(path, '_blank');
      } else {
        // פתיחת קישור חיצוני באותו חלון (למשל מדריך מוניטורינג)
        const serverUrl = import.meta.env.VITE_SERVER_URL || 'https://shanenli-server.fly.dev';
        window.location.href = `${serverUrl}${path}`;
      }
    } else {
      navigate(path);
    }
    setMobileMenuAnchor(null); // סגירת התפריט במובייל
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleOpenAvatarMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarMenuAnchor(event.currentTarget);
  };

  const handleCloseAvatarMenu = () => {
    setAvatarMenuAnchor(null);
  };

  const handleAvatarMenuItemClick = (action: string) => {
    if (action === 'profile') {
      setOpenProfileModal(true);
    } else if (action === 'backToApp') {
      handleExitToApp();
    } else if (action === 'logout') {
      setOpenLogoutDialog(true);
    }
    handleCloseAvatarMenu();
  };

  const handleCloseProfileModal = () => {
    setOpenProfileModal(false);
  };

  const handleCloseLogoutDialog = () => {
    setOpenLogoutDialog(false);
  };

  const handleConfirmLogout = () => {
    userStore.userData = null;
    localStorage.clear();
    sessionStorage.clear();
    navigate('/');
    setOpenLogoutDialog(false);
  };

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const toggleSchoolSelector = () => {
    setSchoolSelectorExpanded(!schoolSelectorExpanded);
  };

  // קבלת מפתח תרגום לפי role ID
  const getRoleTranslationKey = (roleId: number | undefined): string => {
    switch (roleId) {
      case ROLES.ADMIN:
        return 'roleAdmin';
      case ROLES.PRINCIPAL:
        return 'rolePrincipal';
      case ROLES.TEACHER:
        return 'roleTeacher';
      case ROLES.STUDENT:
        return 'roleStudent';
      default:
        return 'unknownRole';
    }
  };

  // רשימת הפריטים בתפריט הניווט - מותאמת לסוג המשתמש
  const menuItems = [
    ...(isPrincipal ? [{
      text: t('principalDashboard'),
      path: '/admin-panel/principal-dashboard',
      icon: <DashboardIcon />,
      roles: ['principal'],
    }] : []),
    {
      text: t('overview'),
      path: '/admin-panel/overview',
      icon: <DashboardIcon />,
      roles: ['admin', 'principal'],
    },
    {
      text: t('comprehensiveOverview'),
      path: '/admin-panel/comprehensive-overview',
      icon: <ComprehensiveIcon />,
      roles: ['admin'],
    },
    {
      text: t('statistics'),
      path: '/admin-panel/statistics',
      icon: <StatisticsIcon />,
      roles: ['admin'],
    },
    {
      text: t('readingSessionsAnalytics'),
      path: '/admin-panel/reading-sessions',
      icon: <MenuBookIcon />,
      roles: ['admin', 'principal'],
    },
    {
      text: isPrincipal ? t('schoolManagement') : t('schoolsManagment'),
      path: '/admin-panel/schools-management',
      icon: <SchoolIcon />,
      roles: ['admin', 'principal'],
    },
    {
      text: t('manageUsers'),
      path: '/admin-panel/users-management',
      icon: <PeopleIcon />,
      roles: ['admin', 'principal'],
    },
    {
      text: t('monitoringGuide'),
      path: '/monitoring-guide',
      icon: <MonitorIcon />,
      roles: ['admin'],
      isExternal: true,
      openInNewTab: false,
    },
    {
      text: t('sentryDashboard'),
      path: 'https://shanenli.sentry.io/dashboards/',
      icon: <BugReportIcon />,
      roles: ['admin'],
      isExternal: true,
      openInNewTab: true,
    },
  ];

  // רשימת הפריטים בתפריט האווטר
  const avatarMenuItems = [
    {
      text: t('profile'),
      action: 'profile',
      icon: <Face className="admin-panel-sidebar__avatar-menu-icon" />,
    },
    {
      text: t('backToApp'),
      action: 'backToApp',
      icon: <ExitToAppIcon className="admin-panel-sidebar__avatar-menu-icon" />,
    },
    {
      text: t('logout'),
      action: 'logout',
      icon: <Logout className="admin-panel-sidebar__avatar-menu-icon" />,
    },
  ];

  const sidebarPosition = isRtl ? 'right' : 'left';

  return (
    <Box className={`admin-panel-sidebar__main-container ${isRtl ? 'admin-panel-sidebar--rtl' : ''}`}>
      {/* סיידבר ניווט */}
      <Drawer
        variant="permanent"
        anchor={sidebarPosition}
        className={`admin-panel-sidebar__drawer ${sidebarExpanded ? 'admin-panel-sidebar__drawer--expanded' : 'admin-panel-sidebar__drawer--collapsed'}`}
        classes={{
          paper: `admin-panel-sidebar__drawer-paper ${sidebarExpanded ? 'admin-panel-sidebar__drawer-paper--expanded' : 'admin-panel-sidebar__drawer-paper--collapsed'} sidebar-transition`
        }}
      >
        {/* כותרת הסיידבר */}
        <SidebarHeader
          expanded={sidebarExpanded}
          onToggle={toggleSidebar}
          onLogoClick={handleExitToApp}
          title={isPrincipal ? t('schoolAdminPanel') : t('adminPanel')}
          isRtl={isRtl}
          t={t}
        />

        {/* סלקטור בית ספר למנהלי בתי ספר ומנהלי מערכת */}
        <SchoolSelector
          schools={adminStore.availableSchools}
          selectedSchoolId={adminStore.selectedSchoolId}
          onSchoolChange={(schoolId) => adminStore.setSelectedSchool(schoolId)}
          expanded={sidebarExpanded}
          onToggle={toggleSchoolSelector}
          isRtl={isRtl}
          t={t}
          showAllSchoolsOption={isAdmin}
          isLoading={adminStore.isLoadingSchools}
        />

        {/* תפריט הניווט - סינון לפי תפקיד */}
        <NavigationMenu
          items={menuItems.filter(item => {
            const userType = isAdmin ? 'admin' : 'principal';
            return item.roles.includes(userType);
          })}
          currentPath={location.pathname}
          onItemClick={(path, isExternal, openInNewTab) => handleMenuItemClick(path, isExternal, openInNewTab)}
          expanded={sidebarExpanded}
          isAdmin={isAdmin}
        />

        {/* אזור המשתמש בתחתית */}
        <UserSection
          expanded={sidebarExpanded}
          userName={userStore.userData?.personalInfo?.name || ''}
          userRole={t(getRoleTranslationKey(userRoleId))}
          avatarId={currentAvatarId}
          userType={isAdmin ? 'admin' : isPrincipal ? 'principal' : 'principal'}
          onAvatarClick={handleOpenAvatarMenu}
          onLanguageToggle={() => {
            const newLang = i18n.language === 'he' ? 'en' : 'he';
            i18n.changeLanguage(newLang);
          }}
          isRtl={isRtl}
          t={t}
        />
      </Drawer>

      {/* תוכן ראשי */}
      <Box className="admin-panel-sidebar__content-area">
        {/* תפריט האווטר - מחובר ישירות לאווטר בסיידבר */}
        <AvatarMenu
          anchorEl={avatarMenuAnchor}
          open={Boolean(avatarMenuAnchor)}
          onClose={handleCloseAvatarMenu}
          onItemClick={handleAvatarMenuItemClick}
          items={avatarMenuItems}
          isRtl={isRtl}
        />

        {/* תפריט המבורגר למובייל */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose}
          PaperProps={{
            className: 'admin-panel-sidebar__mobile-menu-paper',
          }}
          transformOrigin={{ horizontal: isRtl ? 'left' : 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: isRtl ? 'left' : 'right', vertical: 'bottom' }}
        >
          {/* סלקטור בית ספר במובייל */}
          {(isPrincipal || isAdmin) && adminStore.availableSchools.length > 0 && (
            <MenuItem className="admin-panel-sidebar__mobile-school-selector-container">
              {adminStore.availableSchools.length === 1 ? (
                <Box className="admin-panel-sidebar__mobile-school-selector-single-display">
                  <Typography variant="body2" color="text.secondary" className="admin-panel-sidebar__mobile-school-selector-label">
                    {t('school')}:
                  </Typography>
                  <Typography variant="body1" className="admin-panel-sidebar__mobile-school-selector-name">
                    {adminStore.availableSchools[0].name}
                  </Typography>
                </Box>
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel className="admin-panel-sidebar__mobile-school-selector-form-label">בית ספר</InputLabel>
                  <Select
                    value={adminStore.selectedSchoolId || ''}
                    onChange={(e) => adminStore.setSelectedSchool(e.target.value === '' ? null : String(e.target.value))}
                    label="בית ספר"
                    inputProps={{ 'aria-label': 'בחירת בית ספר' }}
                    IconComponent={() => null}
                    className="admin-panel-sidebar__mobile-school-selector-select"
                  >
                    <MenuItem key="all-schools" value="" className="admin-panel-sidebar__mobile-school-selector-all-option">
                      {t('allSchools')}
                    </MenuItem>
                    {adminStore.availableSchools.map((school: any) => (
                      <MenuItem key={school.id} value={String(school.id)} className="admin-panel-sidebar__mobile-school-selector-option">
                        {school.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </MenuItem>
          )}

          {menuItems
            .filter(item => {
              const userType = isAdmin ? 'admin' : 'principal';
              return item.roles.includes(userType);
            })
            .map((item) => (
            <MenuItem
              key={item.text}
              onClick={() => handleMenuItemClick(item.path, item.isExternal, item.openInNewTab)}
              selected={location.pathname === item.path}
              className="admin-panel-sidebar__mobile-nav-menu-item"
            >
              <ListItemIcon className="admin-panel-sidebar__mobile-nav-menu-icon">{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </MenuItem>
          ))}
        </Menu>

        {/* מודאל הפרופיל */}
        <ProfileModal isOpen={openProfileModal} onClose={handleCloseProfileModal} />

        {/* מודאל התנתקות */}
        <LogoutModal isOpen={openLogoutDialog} onClose={handleCloseLogoutDialog} onConfirm={handleConfirmLogout} />

        {/* תוכן העמוד */}
        {children}
      </Box>
    </Box>
  );
};

export default observer(AdminPanelSidebar);